import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAnonymousUser } from '@/hooks/useAnonymousUser';

// Global state to force updates across all hook instances
let globalUpdateTrigger = 0;
const updateListeners: (() => void)[] = [];

const triggerGlobalUpdate = () => {
  globalUpdateTrigger++;
  updateListeners.forEach(listener => listener());
};

export interface Cast {
  id: string;
  user: {
    name: string;
    username: string;
    avatar: string;
    verified: boolean;
  };
  iso_request: string;
  location_radius_km: number;
  broadcast_location_lat: number;
  broadcast_location_lng: number;
  location_address: string;
  sentiment: string;
  likes_count: number;
  comments_count: number;
  created_at: string;
  solved: boolean;
  ad_placement_duration_days?: number;
  ad_placement_expires_at?: string;
  isLiked?: boolean;
  calculatedDistance?: number;
  distanceText?: string;
}

export const useCasts = () => {
  const [casts, setCasts] = useState<Cast[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { anonymousUser } = useAnonymousUser();

  const fetchCasts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 useCasts - Starting to fetch casts...');
      
      // Get current user to check likes
      const { data: { user } } = await supabase.auth.getUser();
      console.log('👤 useCasts - Current user:', user?.id || 'No user');
      
      // Fetch casts first (no join to avoid schema dependency)
      const { data: castsData, error: castsError } = await supabase
        .from('casts')
        .select('id,user_id,iso_request,location_radius_km,broadcast_location_lat,broadcast_location_lng,location_address,sentiment,likes_count,comments_count,created_at,solved,ad_placement_duration_days,ad_placement_expires_at')
        .order('created_at', { ascending: false })
        .limit(25);

      console.log('📊 useCasts - Raw casts data:', castsData);
      console.log('❌ useCasts - Casts error:', castsError);

      if (castsError) {
        console.error('Error fetching casts:', castsError);
        setError(castsError.message);
        return;
      }

      // Fetch related profiles separately using user_id to avoid failing join hints
      const userIds = Array.from(new Set((castsData || []).map((c: any) => c.user_id).filter(Boolean)));
      let profilesMap = new Map<string, any>();
      if (userIds.length > 0) {
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('user_id, username, farm_name, avatar_url, location')
          .in('user_id', userIds);

        if (profilesError) {
          console.warn('⚠️ useCasts - Profiles fetch error (continuing without profiles):', profilesError);
        } else {
          profilesMap = new Map((profilesData || []).map((p: any) => [p.user_id, p]));
        }
      }

      if (castsError) {
        console.error('Error fetching casts:', castsError);
        setError(castsError.message);
        return;
      }

      // If we have casts, get like status for current user
      let userLikes: string[] = [];
      if (user && castsData && castsData.length > 0) {
        const { data: likesData } = await supabase
          .from('cast_likes')
          .select('cast_id')
          .eq('user_id', user.id)
          .in('cast_id', castsData.map(c => c.id));
        
        userLikes = likesData?.map(like => like.cast_id) || [];
        console.log('👍 useCasts - User likes:', userLikes);
      }

      // Transform the data to match the Cast interface
      const transformedCasts: Cast[] = (castsData || []).map((cast: any) => {
        const profile = profilesMap.get(cast.user_id);
        return {
          id: cast.id,
          user: {
            name: profile?.farm_name || 'Unknown Farm',
            username: profile?.username || 'unknown',
            avatar: profile?.avatar_url || "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
            verified: Math.random() > 0.5, // Random for now, can be added to database later
          },
          iso_request: cast.iso_request,
          location_radius_km: cast.location_radius_km,
          broadcast_location_lat: parseFloat(cast.broadcast_location_lat),
          broadcast_location_lng: parseFloat(cast.broadcast_location_lng),
          location_address: profile?.location || cast.location_address,
          sentiment: cast.sentiment,
          likes_count: cast.likes_count || 0,
          comments_count: cast.comments_count || 0,
          created_at: formatTimestamp(cast.created_at),
          solved: cast.solved || false,
          ad_placement_duration_days: cast.ad_placement_duration_days,
          ad_placement_expires_at: cast.ad_placement_expires_at,
          isLiked: userLikes.includes(cast.id)
        };
      });

      console.log('✅ useCasts - Transformed casts:', transformedCasts);
      setCasts(transformedCasts);
      console.log(`✅ Successfully fetched ${transformedCasts.length} casts`);
    } catch (err) {
      console.error('❌ useCasts - Error fetching casts:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    if (diffInHours < 48) return '1 day ago';
    return `${Math.floor(diffInHours / 24)} days ago`;
  };

  const addCast = async (castData: {
    iso_request: string;
    location_radius_km: number;
    broadcast_location_lat: number;
    broadcast_location_lng: number;
    location_address: string;
    sentiment?: string;
    ad_placement_duration_days?: number;
  }) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Check if user has a profile
      const { data: userProfile, error: profileError } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (profileError && profileError.code !== 'PGRST116') {
        console.error('❌ Error checking user profile:', profileError);
        throw new Error('Failed to verify user profile');
      }

      if (!userProfile) {
        throw new Error('Please complete your profile setup before creating an ISO cast');
      }

      console.log('📡 Adding cast to database:', castData);

      // Calculate expiration date based on duration
      const expirationDate = castData.ad_placement_duration_days 
        ? new Date(Date.now() + castData.ad_placement_duration_days * 24 * 60 * 60 * 1000).toISOString()
        : null;

      const castDataToInsert = {
        user_id: user.id,
        iso_request: castData.iso_request,
        location_radius_km: castData.location_radius_km,
        broadcast_location_lat: castData.broadcast_location_lat,
        broadcast_location_lng: castData.broadcast_location_lng,
        location_address: castData.location_address,
        sentiment: castData.sentiment || 'standard',
        ad_placement_duration_days: castData.ad_placement_duration_days || 7,
        ad_placement_expires_at: expirationDate,
        likes_count: 0,
        comments_count: 0,
        solved: false
      };

      const { data, error } = await supabase
        .from('casts')
        .insert(castDataToInsert)
        .select()
        .single();

      if (error) {
        console.error('Database insert error:', error);
        throw error;
      }

      console.log('✅ Cast added successfully:', data);

      // Refresh casts after adding
      await fetchCasts();
      return data;
    } catch (err) {
      console.error('Error adding cast:', err);
      throw err;
    }
  };

  const toggleSolved = async (castId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      console.log('🔄 Toggling solved status for cast:', castId);
      console.log('👤 Current user:', user?.id);

      // Get current cast to check current solved status
      const cast = casts.find(c => c.id === castId);
      if (!cast) {
        console.error('Cast not found');
        return;
      }

      if (user) {
        // Authenticated user flow - direct database interaction
        const { data: castFromDb, error: fetchError } = await supabase
          .from('casts')
          .select('user_id, solved')
          .eq('id', castId)
          .single();

        if (fetchError) {
          console.error('Error fetching cast:', fetchError);
          throw new Error('Could not fetch cast');
        }

        const newSolvedStatus = !castFromDb.solved;
        console.log(`📝 Updating solved status from ${castFromDb.solved} to ${newSolvedStatus}`);

        const { error: updateError } = await supabase
          .from('casts')
          .update({ solved: newSolvedStatus })
          .eq('id', castId);

        if (updateError) {
          console.error('Error updating solved status:', updateError);
          throw updateError;
        }
      } else {
        // Anonymous user flow - use Edge Function for validation
        if (!anonymousUser) {
          console.warn('No anonymous user available for marking solved');
          return;
        }

        const { error } = await supabase.functions.invoke('anonymous-interactions', {
          body: {
            action: 'mark_solved',
            anonymousUserId: anonymousUser.id,
            sessionId: anonymousUser.sessionId,
            castId
          }
        });

        if (error) {
          console.error('Error marking cast as solved:', error);
          throw error;
        }
      }

      console.log('✅ Successfully updated solved status');

      // Trigger global update to sync across all hook instances
      triggerGlobalUpdate();

      // Refresh from database to ensure consistency
      await fetchCasts();
    } catch (err) {
      console.error('Error toggling solved status:', err);
      throw err;
    }
  };

  const toggleLike = async (castId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        // Authenticated user flow - direct database interaction
        const { data: existingLike } = await supabase
          .from('cast_likes')
          .select('id')
          .eq('cast_id', castId)
          .eq('user_id', user.id)
          .single();

        if (existingLike) {
          await supabase
            .from('cast_likes')
            .delete()
            .eq('cast_id', castId)
            .eq('user_id', user.id);
        } else {
          await supabase
            .from('cast_likes')
            .insert({
              cast_id: castId,
              user_id: user.id
            });
        }
      } else {
        // Anonymous user flow - use Edge Function
        if (!anonymousUser) {
          console.warn('No anonymous user available for like');
          return;
        }

        const { error } = await supabase.functions.invoke('anonymous-interactions', {
          body: {
            action: 'toggle_cast_like',
            anonymousUserId: anonymousUser.id,
            sessionId: anonymousUser.sessionId,
            castId
          }
        });

        if (error) {
          console.error('Error with anonymous cast like:', error);
          throw error;
        }
      }

      // Refresh casts to get updated counts and like status
      await fetchCasts();
    } catch (err) {
      console.error('Error toggling cast like:', err);
      throw err;
    }
  };

  useEffect(() => {
    fetchCasts();
    
    // Add this instance to the global update listeners
    const updateListener = () => fetchCasts();
    updateListeners.push(updateListener);
    
    // Set up real-time subscription for cast updates
    const channel = supabase
      .channel('casts-changes')
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'casts'
        },
        (payload) => {
          console.log('🔄 Real-time cast update received:', payload);
          // Refresh casts when any cast is modified
          fetchCasts();
        }
      )
      .subscribe();

    return () => {
      console.log('🧹 Cleaning up casts real-time subscription');
      // Remove from global listeners
      const index = updateListeners.indexOf(updateListener);
      if (index > -1) {
        updateListeners.splice(index, 1);
      }
      supabase.removeChannel(channel);
    };
  }, []);

  return {
    casts,
    loading,
    error,
    fetchCasts,
    addCast,
    toggleLike,
    toggleSolved
  };
};
